import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  leaveDateRange,
  leaveKind,
  leaveStart,
  leaveStatusTab,
  leaveTimeFrame,
  leaveTone,
  statusKey,
} from "../leaves/leaveTone";

/* The detail behind the dashboard's leave tile, opened by its "View more":
   one list, no tabs, so "1 pending" can be answered with which one without
   leaving the page.

   Scoped the way the tile is: leaves falling in `year`, plus every request
   still pending whatever year it is for - a pending January leave applied
   for in December is exactly the one the employee is waiting on. Pending
   rows come first, then the rest newest first. The full history stays on
   My Leaves, linked at the bottom.

   `breakdown` is the tile's taken-by-kind split (`[{ label, value, sub }]`),
   shown as a strip above the list: it is detail, so it lives behind
   "View more" and the tile keeps only its three figures. */
const isPending = (leave) => leaveStatusTab(leave)?.value === "pending";

export const leaveSummaryRows = (leaves, year) =>
  (leaves ?? [])
    .filter((leave) => {
      if (isPending(leave)) return true;
      const start = leaveStart(leave);
      return start.isValid() && start.year() === year;
    })
    .sort((a, b) => {
      if (isPending(a) !== isPending(b)) return isPending(a) ? -1 : 1;
      return (Number(b?.from_timestamp) || 0) - (Number(a?.from_timestamp) || 0);
    });

const LeaveSummaryList = ({ leaves, year, breakdown }) => {
  const rows = useMemo(() => leaveSummaryRows(leaves, year), [leaves, year]);

  return (
    <div className="lsl">
      {breakdown && breakdown.length ? (
        <dl className="lsl-kinds" aria-label={"Leave taken in " + year + " by kind"}>
          {breakdown.map((part) => (
            <div className="lsl-kind-part" key={part.label}>
              <dt>{part.label}</dt>
              <dd>
                <span className="lsl-kind-value">{part.value}</span>
                {part.sub ? <span className="lsl-muted">{part.sub}</span> : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {rows.length ? (
        <ul className="lsl-list">
          {rows.map((leave, index) => {
            const tone = leaveTone(leave);
            const frame = leaveTimeFrame(leave);
            return (
              <li
                key={leave?.id ?? index}
                className={"lsl-row" + (tone ? " lsl-tone-" + tone : "")}
              >
                <div className="lsl-what">
                  <div className="lsl-kind">
                    {leaveKind(leave?.type)}
                    {leave?.duration ? (
                      <span className="lsl-muted"> · {leave.duration}</span>
                    ) : null}
                  </div>
                  {leave?.reason ? (
                    <div className="lsl-reason">{leave.reason}</div>
                  ) : null}
                </div>
                <div className="lsl-when">
                  <div>{leaveDateRange(leave) || "-"}</div>
                  {frame ? <div className="lsl-muted">{frame}</div> : null}
                </div>
                <div className="lsl-status">
                  <span className={"status-pill s-" + statusKey(leave?.status)}>
                    {leave?.status}
                  </span>
                  {leave?.comments ? (
                    <div className="lsl-muted lsl-comment">{leave.comments}</div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="lsl-empty">No leave in {year} yet.</p>
      )}
      <Link to="/my-leave" className="lsl-more">
        Full leave history
        <i className="fa fa-arrow-right" aria-hidden="true"></i>
      </Link>
    </div>
  );
};

export default LeaveSummaryList;
